<template>
  <div>
    <!-- S I G N U P  T I T L E -->
    <div class="text-center">
      <p class="text-24 md:text-47 font-bold text-app-color">One last thing...</p>
      <p class="text-20 text-grey-20">What do you prefer?</p>
    </div>

    <div class="flex justify-center mt-30">
      <div class="mr-10 cursor-pointer" @click="updateTheme('light')">
        <p class="font-bold ml-2 text-15">Light mode</p>
        <img
          class="w-120 mt-3 rounded-10"
          :class="theme === 'light' ? 'border border-brand shadow-signup' : 'border border-transparent'"
          :src="require(`@/assets/images/signup/light-${theme}.svg`)" alt="light"
        >
      </div>
      <div class="ml-10 cursor-pointer" @click="updateTheme('dark')">
        <p class="font-bold ml-2 text-15">Dark mode</p>
        <img
          class="w-120 mt-3 rounded-10"
          :class="theme === 'dark' ? 'border border-brand shadow-signup' : 'border border-transparent'"
          :src="require(`@/assets/images/signup/dark.svg`)" alt="dark"
        >
      </div>
    </div>

    <div class="flex justify-center mt-25">
      <div class="mr-10 cursor-pointer" @click="updatePaperMode('Paper shape')">
        <p class="font-bold ml-2 text-15">Paper</p>
        <img
          class="w-120 mt-3 rounded-10"
          :class="paperMode === 'Paper shape' ? 'border border-brand shadow-signup' : 'border border-transparent'"
          :src="require(`@/assets/images/signup/paper-${theme}.svg`)" alt="paper"
        >
      </div>
      <div class="ml-10 cursor-pointer" @click="updatePaperMode('Paperless')">
        <p class="font-bold ml-2 text-15">Paperless</p>
        <img
          class="w-120 mt-3 rounded-10"
          :class="paperMode === 'Paperless' ? 'border border-brand shadow-signup' : 'border border-transparent'"
          :src="require(`@/assets/images/signup/paperless-${theme}.svg`)" alt="paperless"
        >
      </div>
    </div>

    <p class="mt-30 text-grey-275 text-center">You can always change this later too.</p>

    <ButtonModule
      text="Go to your homepage"
      class="w-full justify-center bg-brand hover:bg-brand-hover text-white rounded-10 mt-20 py-16 font-medium text-17"
      @click="register"
    />

    <SignupLoading v-if="showLastStep" :initial-value="15" />

    <RestoreAccount ref="restoreModalRef" :email="user.email" :date="deletedAt" title="Account already exists">
      <p class="text-14 mt-20 mb-25">
        Account with this email already exists and was recently deleted.
        If you want to restore this account, please type a password.
      </p>
    </RestoreAccount>
  </div>
</template>

<script>
import { computed, defineComponent, ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useGtag } from 'vue-gtag-next'

import { settingsService, profileService } from '@/services'

import SignupLoading from '@/components/auth/SignupLoading'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import RestoreAccount from '@/components/auth/RestoreAccount'

export default defineComponent({
  name: 'SignUpTeams',

  components: { ButtonModule, SignupLoading, RestoreAccount },

  props: {
    user: Object,
    hasAccount: (String || null)
  },

  setup (props) {
    const { state, commit, dispatch } = useStore()
    const route = useRoute()
    const router = useRouter()

    const showLastStep = ref(false)
    const restoreModalRef = ref(null)

    const settings = computed(() => state.settings.settings)
    const userProfile = computed(() => state.profile.user)

    const theme = ref('light')
    const paperMode = ref('Paper shape')
    const deletedAt = ref(null)

    onMounted(() => {
      commit('setRequestCount', 0)
      updateSettings()
    })

    function updateTheme (value) {
      theme.value = value
      updateSettings()
    }

    function updatePaperMode (value) {
      paperMode.value = value
      updateSettings()
    }

    function updateSettings () {
      const thm = theme.value === 'light' ? 'Magenta' : 'Dark Purple'
      const design = {
        ...settings.value,
        theme: thm,
        mode: paperMode.value
      }

      commit('updateSettings', design)
    }

    async function register () {
      try {
        showLastStep.value = true

        if (props.hasAccount) {
          const user = await dispatch('getProfile')

          await profileService.update({
            firstName: user.profile.firstName.trim(),
            lastName: user.profile.lastName.trim(),
            email: user.email,
            hasPersonal: true
          })
          await dispatch('createDefaultProjects')
        } else {
          await dispatch('signUp', {
            firstName: props.user.firstName.trim(),
            lastName: props.user.lastName.trim(),
            email: props.user.email.trim(),
            password: props.user.password
          })

          settingsService.update(settings.value)
        }

        await dispatch('getProfile')

        const { event } = useGtag()
        event('conversion', { send_to: 'AW-597169833/trJoCIeeiuoBEKmt4JwC' })
        await settingsService.update(settings.value)

        goToStripe()
      } catch ({ data }) {
        showLastStep.value = false
        if (data?.error?.code === 6) {
          deletedAt.value = data.error.message
          restoreModalRef.value.open()
        }
      }

      // window.fbq('trackCustom', 'signupPersonal')
    }

    function goToStripe () {
      if (route.query.planId) {
        const stripe = window.Stripe(process.env.VUE_APP_STRIPE)
        const data = {
          planId: route.query.planId,
          userId: userProfile.value.id
        }
        dispatch('upgradePlan', data).then(res => {
          commit('loading', true)
          stripe.redirectToCheckout({ sessionId: res })
            .then(() => {})
            .catch(() => commit('loading', false))
        })
      } else {
        router.push('/')
      }
    }

    return {
      // refs
      restoreModalRef,
      // data
      theme,
      paperMode,
      deletedAt,
      showLastStep,
      //  functions
      register,
      updateTheme,
      updateSettings,
      updatePaperMode
    }
  }
})
</script>
